/**
 * One browser consumer for an MJPEG multipart body.
 * A clean HTTP 200 end does not fire img.onerror, so the page has to read
 * the body itself and notice when it finishes.
 */

export type MjpegRelayResult = 'ended' | 'error' | 'aborted';

export type MjpegRelayArgs = {
  url: string;
  signal: AbortSignal;
  onFrame: (jpeg: Uint8Array) => void;
  fetchImpl?: typeof fetch;
};

const HEADER_BREAK = new Uint8Array([13, 10, 13, 10]);

function findBytes(haystack: Uint8Array, needle: Uint8Array, from = 0): number {
  if (!needle.length || haystack.length < needle.length) return -1;
  for (let i = from; i <= haystack.length - needle.length; i += 1) {
    let matched = true;
    for (let j = 0; j < needle.length; j += 1) {
      if (haystack[i + j] !== needle[j]) {
        matched = false;
        break;
      }
    }
    if (matched) return i;
  }
  return -1;
}

function asciiHeader(bytes: Uint8Array): string {
  let text = '';
  for (let i = 0; i < bytes.length; i += 1) text += String.fromCharCode(bytes[i]);
  return text;
}

function concatBytes(left: Uint8Array, right: Uint8Array): Uint8Array {
  if (!left.length) return right;
  if (!right.length) return left;
  const out = new Uint8Array(left.length + right.length);
  out.set(left, 0);
  out.set(right, left.length);
  return out;
}

export function takeMultipartJpegFrames(buffer: Uint8Array): {
  frames: Uint8Array[];
  rest: Uint8Array;
} {
  const frames: Uint8Array[] = [];
  let rest = buffer;
  while (rest.length) {
    const headerEnd = findBytes(rest, HEADER_BREAK);
    if (headerEnd < 0) break;
    const header = asciiHeader(rest.subarray(0, headerEnd));
    const match = /content-length:\s*(\d+)/i.exec(header);
    if (!match) break;
    const length = Number(match[1]);
    if (!Number.isFinite(length) || length < 0) break;
    const bodyStart = headerEnd + HEADER_BREAK.length;
    if (rest.length < bodyStart + length) break;
    frames.push(rest.slice(bodyStart, bodyStart + length));
    let next = bodyStart + length;
    if (rest[next] === 13 && rest[next + 1] === 10) next += 2;
    rest = rest.subarray(next);
  }
  return { frames, rest };
}

export async function relayMultipartMjpeg(args: MjpegRelayArgs): Promise<MjpegRelayResult> {
  const fetchImpl = args.fetchImpl ?? fetch;
  let response: Response;
  try {
    response = await fetchImpl(args.url, {
      signal: args.signal,
      credentials: 'include',
      cache: 'no-store',
    });
  } catch {
    if (args.signal.aborted) return 'aborted';
    return 'error';
  }
  if (!response.ok || !response.body) return 'error';
  const reader = response.body.getReader();
  let pending = new Uint8Array(0);
  try {
    while (!args.signal.aborted) {
      const chunk = await reader.read();
      if (chunk.done) return 'ended';
      if (!chunk.value || !chunk.value.length) continue;
      pending = concatBytes(pending, chunk.value);
      const taken = takeMultipartJpegFrames(pending);
      pending = taken.rest;
      taken.frames.forEach((frame) => args.onFrame(frame));
    }
    return 'aborted';
  } catch {
    if (args.signal.aborted) return 'aborted';
    return 'error';
  } finally {
    try {
      reader.releaseLock();
    } catch {
      // The reader may already be released after a clean end.
    }
  }
}

/** Jest mounts the live pane without a broker. Production always reads the body. */
export function defaultMjpegStreamRelay(args: MjpegRelayArgs): Promise<MjpegRelayResult> {
  if (typeof process !== 'undefined' && process.env && process.env.JEST_WORKER_ID) {
    return new Promise((resolve) => {
      if (args.signal.aborted) {
        resolve('aborted');
        return;
      }
      args.signal.addEventListener('abort', () => resolve('aborted'), { once: true });
    });
  }
  return relayMultipartMjpeg(args);
}
